import { StringUtil } from "@mcleod/core/src/StringUtil";
import { AutogenLayoutImportSnapshot } from "./autogen/AutogenLayoutImportSnapshot";
import { ChangeEvent, Checkbox, ClickEvent, Panel, SelectionType, Table, TableRow, TableRowDisplayEvent, Textbox } from "@mcleod/components";
import { Api, ModelRow } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";

export class ImportSnapshot extends AutogenLayoutImportSnapshot {

  private _parentRecordTableName: string;
  private _parentRecordId: string;
  private _snapshotFilename: string;
  private _fields: string[] = ["company_id", "transaction_set", "partner_name",
    "partner_id", "alt_partner_id", "version", "direction"]

  private _onImportCompleteCallback?: (parentRecordTableName: string, parentRecordId: string) => void;

  /**
   * Sets a callback to be executed after import completion
   */
  public setOnImportCompleteCallback(callback: (parentRecordTableName: string, parentRecordId: string) => void): void {
    this._onImportCompleteCallback = callback;
  }

  async onLoad() {
    // Initialize the table if needed
    await super.onLoad();
  }

  onTableImportCellClick(event: ClickEvent) {
    // Handle checkbox clicks similar to the Java implementation
    const row = this.tableImport.dataSource.activeRow;

    if (row) {
      // When Apply checkbox is clicked, handle the Create New ID logic
      const isApplied = row.get('apply_change');
      if (isApplied) {
        const fileName = row.get('_fileName') as string;
        if (fileName && (fileName.includes('commControl') ||
          fileName.includes('template') ||
          fileName.includes('HttpParamTemplate'))) {
          row.set('new_id', 'N');
        }
      } else {
        row.set('new_id', null);
      }
    }
  }

  /** This is an event handler for the onRowDisplay event of tableImport. */
  tableImportOnRowDisplay(event: TableRowDisplayEvent) {
    const tableRow = event.getTableRow() as TableRow;
    tableRow.table.selectRow(tableRow, SelectionType.DISTINCT, null);
    const rowData = tableRow.data;
    if (StringUtil.isEmptyString(this._parentRecordTableName)) {
      this._parentRecordTableName = rowData.get("parent_record_table_name");
    }
    if (StringUtil.isEmptyString(this._parentRecordId)) {
      this._parentRecordId = rowData.get("parent_record_id");
    }
    const checkboxNewId = tableRow.findComponentById("checkboxNewId") as Checkbox;
    const checkboxApplyChange = tableRow.findComponentById("checkboxApplyChange") as Checkbox;
    checkboxNewId.visible = !StringUtil.isEmptyString(rowData.get("_idFieldName"));
    checkboxNewId.checked = !StringUtil.isEmptyString(rowData.get("_idFieldName"));
    checkboxApplyChange.checked = true;
  }

  /** This is an event handler for the onClick event of buttonImportSnapshot. */
  async buttonImportSnapshotOnClick(event: ClickEvent) {
    this.buttonImportSnapshot.busy = true;
    try {
      // Collect all row data from tableImport
      const data = this.tableImport.dataSource.data;
      const importData: ModelRow[] = [];

      if (await this.hasExitingProfile()) {
        await CommonDialogs.showDialog(`A profile already exists for this record: ${this.formatSnapshotFilename()}`);
        this.buttonImportSnapshot.busy = false;
        return;
      }
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        if (row) {
          // If the table row has apply_change checked, include it in the import data
          const tableRow = this.tableImport.getRowByIndex(i);
          if (tableRow && row.get('apply_change')) {
            importData.push(row);
          }
        }
      }

      // Call the API with the collected data
      await Api.post("lme/datafusion/import-snapshot", {
        model_list: importData
      }).then(async response => {
        this.buttonImportSnapshot.busy = false;
        const result = response.data[0]["results"];
        const errors = response.data[0]["errors"];
        if (errors && errors.length > 0) {
          await CommonDialogs.showDialog("Errors occurred during import: " + errors);
        } else if (result && result.length > 0) {
          // Create a panel to properly display the HTML table
          const resultsPanel = new Panel();

          // Create a container div for the button and results
          const containerDiv = document.createElement('div');

          // Create the "Update Next Batch Values" button
          const updateBatchButton = document.createElement('button');
          updateBatchButton.textContent = 'Update Next Batch Values';
          updateBatchButton.style.cssText = `
          float: right;
          margin-bottom: 10px;
          padding: 8px 12px;
          background-color: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        `;

          // Add hover effect
          updateBatchButton.onmouseover = () => {
            updateBatchButton.style.backgroundColor = '#0056b3';
          };
          updateBatchButton.onmouseout = () => {
            updateBatchButton.style.backgroundColor = '#007bff';
          };

          // Add click handler
          updateBatchButton.onclick = async () => {
            try {
              await Api.post("lme/datafusion/update-next-batch", {
                profile_id: this._parentRecordId,
                profile_table: this._parentRecordTableName
              });
              CommonDialogs.showMessage("Next batch values have been updated successfully.");
            } catch (error) {
              console.error('Error updating next batch values:', error);
              CommonDialogs.showMessage("Error updating next batch values. Please try again.");
            }
          };

          // Create a div for the results content
          const resultsDiv = document.createElement('div');
          resultsDiv.innerHTML = result;
          resultsDiv.style.clear = 'both'; // Clear the float
          resultsDiv.style.maxHeight = '600px'; // Set max height for the table area
          resultsDiv.style.overflowY = 'auto'; // Make the table area scrollable

          // Add button and results to container
          containerDiv.appendChild(updateBatchButton);
          containerDiv.appendChild(resultsDiv);

          // Set the container as the panel content
          resultsPanel._element.appendChild(containerDiv);

          // Style the panel to make the table look nice
          resultsPanel._element.style.height = "100%";
          resultsPanel._element.style.overflowY = "visible";
          resultsPanel._element.style.padding = "10px";
          resultsPanel._element.style.boxSizing = "border-box";

          // Add some basic table styling
          const style = document.createElement("style");
          style.textContent = `
          .import-results table {
            border-collapse: collapse;
            width: 100%;
            margin: 10px 0;
          }
          .import-results th, .import-results td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          .import-results th {
            background-color: #f2f2f2;
            font-weight: bold;
          }
          .import-results tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .import-results ul {
            margin: 5px 0;
            padding-left: 20px;
          }
          .import-results li {
            margin: 2px 0;
            line-height: 1.2;
          }
        `;
          document.head.appendChild(style);

          // Add CSS class to the panel for styling
          resultsPanel._element.className = "import-results";

          CommonDialogs.showDialog(resultsPanel, {
            title: "Import Snapshot Results",
            width: "700",
            height: "850",
            resizable: true
          }).then(() => {
            // Call the import complete callback if it exists
            if (this._onImportCompleteCallback && this._parentRecordTableName && this._parentRecordId) {
              console.log("Calling import complete callback with:", {
                parentRecordTableName: this._parentRecordTableName,
                parentRecordId: this._parentRecordId
              });

              // Use setTimeout to ensure the slideout has fully closed before opening the profile
              setTimeout(() => {
                this._onImportCompleteCallback(this._parentRecordTableName, this._parentRecordId);
              }, 100);
            }

            this.slideOut();
          });
        }
      });
    } catch (error) {
      console.error('Error importing snapshot:', error);
      // You might want to show an error dialog here
    }
  }

  public get parentRecordId(): string {
    return this._parentRecordId;
  }

  public set parentRecordId(value: string) {
    this._parentRecordId = value;
  }

  public get parentRecordTableName(): string {
    return this._parentRecordTableName;
  }

  public set parentRecordTableName(value: string) {
    this._parentRecordTableName = value;
  }

  public async hasExitingProfile(): Promise<boolean> {
    const fieldsMap = this.parseSnapshotName(this._snapshotFilename, this._fields);
    if (fieldsMap.size > 0) {
      const transactionSet = fieldsMap.get("transaction_set");
      const profileTable = this.getTransactionType(transactionSet || "");
      if (
        !StringUtil.isEmptyString(profileTable) &&
        !StringUtil.isEmptyString(fieldsMap.get("partner_id")) &&
        !StringUtil.isEmptyString(fieldsMap.get("version")) &&
        !StringUtil.isEmptyString(fieldsMap.get("direction")) &&
        !StringUtil.isEmptyString(fieldsMap.get("alt_partner_id"))
      ) {
        const searchParams: any = {
          partner_id: fieldsMap.get("partner_id"),
          version: fieldsMap.get("version"),
          direction: fieldsMap.get("direction"),
          alt_partner_id: fieldsMap.get("alt_partner_id")
        };
        const response = await Api.search(profileTable, searchParams);
        const results = response.data;
        if (results.length > 0) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Formats the snapshot filename for display by removing underscores and .zip extension
   * Only displays up to the 7th node (separated by underscores)
   */
  private formatSnapshotFilename(): string {
    if (!this._snapshotFilename) {
      return 'Unknown';
    }

    // Remove .zip extension
    let formatted = this._snapshotFilename.replace(/\.zip$/i, '');

    // Split by underscores, take only first 7 parts, then join with spaces
    const parts = formatted.split('_');
    const limitedParts = parts.slice(0, 7);
    formatted = limitedParts.join(' ');

    return formatted;
  }

  public get snapshotFilename(): string {
    return this._snapshotFilename;
  }
  public set snapshotFilename(value: string) {
    this._snapshotFilename = value;
  }

  /**
   * Updates checkboxes for rows based on their selection state
   */
  private updateCheckboxesForSelectedRows(selected: boolean) {
    // Iterate through all rows and set the Apply checkbox
    const rows = this.tableImport.rows as TableRow[];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row) {
        const checkBoxApplyChange = row.findComponentById("checkboxApplyChange") as Checkbox;
        if (checkBoxApplyChange && checkBoxApplyChange.visible) {
          checkBoxApplyChange.checked = selected;
        }
      }
    }
  }

  /** This is an event handler for the onClick event of buttonSelectAll. */
  buttonSelectAllOnClick(event: ClickEvent) {
    this.updateCheckboxesForSelectedRows(true);
  }

  /** This is an event handler for the onClick event of buttonUnselectAll. */
  buttonUnselectAllOnClick(event: ClickEvent) {
    this.updateCheckboxesForSelectedRows(false);
  }

  /**
 * Parses a snapshot filename into field components
 * @param snapshotFileName The snapshot filename to parse
 * @param fields Array of field names to map tokens to
 * @returns Map containing parsed field values
 */
  public parseSnapshotName(snapshotFileName: string, fields: string[]): Map<string, string> {
    const underscore = "_";
    const result = new Map<string, string>();

    // Remove .zip extension if present
    let snapshotName = snapshotFileName;
    if (snapshotName.endsWith(".zip")) {
      snapshotName = snapshotName.substring(0, snapshotName.length - 4);
    }

    // Split by underscores and process tokens
    const tokens = snapshotName.split(underscore);
    let fieldIndex = 0;

    for (const token of tokens) {
      if (fieldIndex < fields.length) {
        result.set(fields[fieldIndex], token);
      } else {
        // Handle overflow tokens for create_date and create_time
        if (!result.has("create_date")) {
          result.set("create_date", token);
        } else if (!result.has("create_time")) {
          result.set("create_time", token);
        }
      }
      fieldIndex++;
    }

    return result;
  }

  /**
   * Determines the transaction type based on the transaction set code
   * @param trxSet The transaction set code (e.g., "204", "214", "210")
   * @returns The transaction type code ("T", "S", "I") or null if unknown
   */
  public getTransactionType(trxSet: string): string | null {
    // Transaction set to type mappings
    const transactionSetMap = new Map<string, string>([
      // Tender/Transport transactions
      ["204", "lme/datafusion/ediorder-profile"], // Motor Carrier Load Tender
      ["856", "lme/datafusion/ediorder-profile"], // Ship Notice/Manifest
      ["858", "lme/datafusion/ediorder-profile"], // Shipment Information
      ["850", "lme/datafusion/ediorder-profile"], // Purchase Order
      ["404", "lme/datafusion/ediorder-profile"], // Rail Carrier Shipment Information

      // Status transactions  
      ["214", "lme/datafusion/edistatus-profile"], // Transportation Carrier Shipment Status Message
      ["322", "lme/datafusion/edistatus-profile"], // Terminal Information

      // Invoice transactions
      ["210", "lme/datafusion/edibilling-profile"], // Motor Carrier Freight Details and Invoice
      ["810", "lme/datafusion/edibilling-profile"],  // Invoice

      ["820", "lme/datafusion/ediremit-profile"],
      ["824", "lme/datafusion/ediremit-profile"]
    ]);

    return transactionSetMap.get(trxSet) || "lme/datafusion/edi-profile";
  }

  onChangeApplyChange(event: ChangeEvent) {
    if (event.userInitiatedChange && event.newValue !== event.oldValue) {
      const checkboxApplyChange = event.target as Checkbox;
      const tableRow = TableRow.getContainingTableRow(checkboxApplyChange);
      const checkboxNewId = tableRow.findComponentById("checkboxNewId") as Checkbox;
      if (checkboxNewId) {
        checkboxNewId.checked = event.newValue;
        checkboxNewId.visible = event.newValue;
      }
    }
  }
}
